import React from 'react';
import {connect} from 'react-redux';
import PropTypes from 'prop-types';
import numeral from 'numeral';
import _ from 'lodash';

import Meta               from 'app/components/common/meta';
import StandardSelect     from 'app/components/common/standard-select';
import CadminLayout       from 'app/components/company-admin/layout/';
import PageLoading        from 'app/components/layout/page-loading';
import GrantSysDuck       from 'app/ducks/company-admin/grant-system';
import format             from 'app/helpers/format';
import helpers            from 'app/helpers/grant-system';
import Ps                 from 'app/helpers/publish-statuses';
import CadminSlx          from 'app/selectors/company-admin/';
import EntitiesSlx        from 'app/selectors/entities';



const {
  AppStatuses,
} = helpers;

const psOptions = [
  {value: null, label: 'All'},
  {value: Ps.ACTIVE, label: Ps.name(Ps.ACTIVE)},
  {value: Ps.ARCHIVED, label: Ps.name(Ps.ARCHIVED)},
];

const friendlyUsd = (num) => {
  if (!_.isFinite(num)) return '';
  return numeral(num / 100).format('$0.0a');
};

class CadminPageGrantBudgets extends React.PureComponent {

  constructor(props) {
    super(props);

    this.state = {
      cattrs: {},
      publishStatus: 'active',
      groupBy: null,
    };

    this.onChangePs = this.onChangePs.bind(this);
  }

  onChangePs(publishStatus) {
    this.setState({publishStatus});
  }
  onChangeCattr(cattrId, optId) {
    this.setState((prevState) => {
      return {cattrs: {...prevState.cattrs,
        [cattrId]: optId,
      }};
    });
  }
  onClickTab(groupBy, event) {
    event.preventDefault();
    this.setState({groupBy});
  }

  getTabs() {
    const {cattrs} = this.props;
    const {groupBy} = this.state;
    return [
      {label: 'Program', value: null},
      ...cattrs.map((cattr) => {
        return {label: cattr.label, value: cattr.id};
      }),
    ];
  }

  getApps() {
    const {allApps, programMap} = this.props;
    if (!allApps) return null;
    const {cattrs, publishStatus} = this.state;
    const cattrPairs = Object.entries(cattrs).filter(([cid, oid]) => !!oid);

    return allApps.filter((app) => {
      const program = programMap[app.grantProgramId];
      if (!program) return false;
      if (publishStatus && program.publishStatus !== publishStatus) return false;
      for (let i = 0; i < cattrPairs.length; i++) {
        const [cattrId, optId] = cattrPairs[i];
        if (program.cattrs[cattrId] !== optId) return false;
      }
      return true;
    });
  }

  getGroupMeta(program) {
    const {groupBy} = this.state;
    const cattr = this.props.cattrs.find(c => c.id === groupBy);
    if (!cattr) return {key: program.id, label: program.title, program};
    const optId = program.cattrs[groupBy] || null;
    const opt = (cattr?.options || []).find(o => o.id === optId) || null;
    const key = opt?.id || 'other';
    const label = opt?.label || 'Other';
    return {key, label};
  }

  makeRow(attrs={}) {
    return {programIds: new Set(), budget: 0, approvedCount: 0, pendingCount: 0, approvedAmount: 0, pendingAmount: 0, ...attrs};
  }
  getReport(apps) {
    const {programMap} = this.props;
    // const baseRow = {programCount: 0, approvedCount: 0, pendingCount: 0, approvedAmount: 0, pendingAmount: 0};
    if (!apps) return null;
    const rowMap = {};
    const sum = this.makeRow();

    apps.forEach((app) => {
      const program = programMap[app.grantProgramId];
      if (!program) return;
      if (app.status === AppStatuses.DECLINED) return;
      const meta = this.getGroupMeta(program);
      if (!rowMap[meta.key]) rowMap[meta.key] = this.makeRow(meta);
      const row = rowMap[meta.key];

      if (!row.programIds.has(program.id)) {
        sum.programIds.add(program.id);
        row.programIds.add(program.id);
        sum.budget += (program.budget || 0);
        row.budget += (program.budget || 0);
      }

      if (app.status === AppStatuses.APPROVED) {
        sum.approvedCount += 1;
        row.approvedCount += 1;
        sum.approvedAmount += (app.amountAwarded || 0);
        row.approvedAmount += (app.amountAwarded || 0);
      } else if (app.status === AppStatuses.REVIEW) {
        sum.pendingCount += 1;
        row.pendingCount += 1;
        sum.pendingAmount += (app.amountRequested || 0);
        row.pendingAmount += (app.amountRequested || 0);
      }
    });

    const rows = Object.values(rowMap).sort((a,b) => {
      if (a.approvedAmount > b.approvedAmount) return -1;
      if (a.approvedAmount < b.approvedAmount) return 1;
      return 0;
    });
    return {sum, rows}
  }

  renderBudget(allocated, spent, grantCount) {
    const percent = allocated ? (Math.min(spent / allocated, 1) * 1) : 1;
    const isOver = spent > allocated;
    const showPercent = percent >= 0.07;
    const left = allocated - spent;

    return (
      <div className={`capg-gbuds-bud ${isOver ? 'over' : 'under'}`}>
        <div className="capg-gbuds-bud-nums">
          <div className="capg-gbuds-bud-nums-num">
            <div className="capg-gbuds-bud-nums-num-val">{format.usd(spent, 'ceil')}</div>
            <div className="capg-gbuds-bud-nums-num-label">{`${grantCount} ${format.pluralize('grant', grantCount)} awarded`}</div>
          </div>
          {isOver ? (<>
            <div className="capg-gbuds-bud-nums-num">
              <div className="capg-gbuds-bud-nums-num-val over">{format.usd(Math.abs(left), 'ceil')}</div>
              <div className="capg-gbuds-bud-nums-num-label over">Over</div>
            </div>
          </>) : (<>
            <div className="capg-gbuds-bud-nums-num">
              <div className="capg-gbuds-bud-nums-num-val">{format.usd(left, 'ceil')}</div>
              <div className="capg-gbuds-bud-nums-num-label">Left</div>
            </div>
          </>)}
          <div className="capg-gbuds-bud-nums-num">
            <div className="capg-gbuds-bud-nums-num-val dark">{format.usd(allocated, 'floor')}</div>
            <div className="capg-gbuds-bud-nums-num-label dark">Budget</div>
          </div>
        </div>
        <div className="capg-gbuds-bud-bar">
          <div className="capg-gbuds-bud-bar-fill" style={{width: `${percent * 100}%`}}>{showPercent ? `${isOver ? '>' : ''}${numeral(percent).format('0%')}` : ''}</div>
        </div>

      </div>
    );
  }

  renderRow(row) {
    const isProgram = !!row.program;
    const programCount = row.programIds.size;

    return (
      <div key={row.key} className="capg-gbuds-row">
        <div  className="capg-gbuds-row-main">
          <div className="capg-gbuds-row-title">{row.label}</div>
          {!isProgram && (
            <div className="capg-gbuds-row-stat">{`Includes ${programCount} ${format.pluralize('program', programCount)}`}</div>
          )}
          <div className="capg-gbuds-row-stat">{`${row.pendingCount} ${format.pluralize('app', row.pendingCount)} in review, ${format.usd(row.pendingAmount, 'round')} requested`}</div>
        </div>
        <div className="capg-gbuds-row-bud">
          {this.renderBudget(row.budget, row.approvedAmount, row.approvedCount)}
        </div>
      </div>
    );
  }

  renderRows(report) {
    if (!report) return 'loading...';
    const {rows} = report;

    return (
      <div>
        {rows.map((row) => {
          return this.renderRow(row);
        })}
      </div>
    );
  }

  render() {
    const {company, programs, cattrs} = this.props;
    if (!company) return <PageLoading />;
    const apps = this.getApps();
    const report = this.getReport(apps);

    return (
      <CadminLayout company={company} activeItem="grant-budgets" className="capg-gbuds">
        <Meta title={`Grant Budgets | Millie`} />
        <div className="ca-main-head">
          <h1 className="ca-main-head-h1">Grant Budgets</h1>
          <div className="ca-main-head-actions">
          </div>
        </div>

        <div className="ca-main-filters">
          <div className="ca-main-filters-filter">
            <label className="ca-main-filters-filter-label">Program Status</label>
            <StandardSelect options={psOptions} value={this.state.publishStatus} onSelect={this.onChangePs} />
          </div>
          {cattrs.map((cattr) => {
            const value = this.state.cattrs[cattr.id];
            return (
              <div key={cattr.id} className="ca-main-filters-filter">
                <label className="ca-main-filters-filter-label">{cattr.label}</label>
                <StandardSelect options={cattr.options} onSelect={this.onChangeCattr.bind(this, cattr.id)} value={value} label="All" allowClear />
              </div>
            );
          })}
        </div>

        <div className="ca-box capg-gbuds-box">
          <div className="ca-box-header">
            <h1 className="ca-box-header-title">Total</h1>
            <div className="ca-box-header-controls"></div>
          </div>
          <div className="ca-box-body">
            {!!report && this.renderRow(report.sum)}
          </div>
        </div>

        <div className="ca-box capg-gbuds-box">
          <div className="ca-box-header">
            <h1 className="ca-box-header-title">Breakdown</h1>
            <div className="ca-box-header-controls"></div>
          </div>
          <div className="ca-box-tabs">
            <div className="ca-box-tabs-tabs">
              {this.getTabs().map((tab, i) => {
                const activeClass = (tab.value === this.state.groupBy) ? 'active' : '';
                return (
                  <a key={i} href="#" className={`ca-box-tabs-tab ${activeClass}`} onClick={this.onClickTab.bind(this, tab.value)}>By {tab.label}</a>
                );
              })}
            </div>
          </div>
          <div className="ca-box-body">
            {this.renderRows(report)}
          </div>
        </div>
      </CadminLayout>
    );
  }

}

const stateToProps = (state) => ({
  company: CadminSlx.company(state),
  programMap: EntitiesSlx.grantPrograms(state),
  // programs: GrantSysDuck.Slx.allPrograms(state),
  programs: GrantSysDuck.Slx.budgetPrograms(state),
  cattrs: GrantSysDuck.Slx.cattrs(state),
  allApps: GrantSysDuck.Slx.allApps(state),
});
const dispatchToProps = (dispatch) => ({});

export default connect(stateToProps, dispatchToProps)(CadminPageGrantBudgets);
